import React, { useEffect, useRef, useLayoutEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import CloseIcon from '~/icons/components/CloseIcon';
import VisuallyHidden from '~/shared/components/VisuallyHidden';
import { AssetContainer } from '~/techstyle-shared/react-assets';
import {
  desktop,
  mobile,
  Button,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

if (process.browser) {
  require('intersection-observer');
}

const Header = styled.div`
  padding: 32px 24px 40px;
  padding-bottom: 14px;
  text-align: center;
  background: ${({ theme }) => theme.colors.compressionOverlayStickyHeader};
  border-top-right-radius: ${({ $isSticky }) => ($isSticky ? '0px' : '16px')};
  border-top-left-radius: ${({ $isSticky }) => ($isSticky ? '0px' : '16px')};
  transition: border-radius 0.2s ease-out;
  overflow: hidden;
  z-index: 999;

  ${mobile`
    position: sticky;
    top: -1px;
    z-index: 999;
  `}

  ${desktop`
    position: relative;
    padding: ${({ theme }) => theme.sizes.xxxl}px 90px;
    padding-bottom: 14px;
  `}
`;

const AssetBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  // required to accurately reflect wrapping height for ref
  height: 100%;

  img {
    min-width: 415px;
  }
`;

const SubheaderAssetWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  img {
    min-width: 415px;
  }

  ${({ $top }) => css`
    top: -${$top}px;
  `}
`;

const HeroContent = styled.div`
  // to overlay background which is position: absolute
  position: relative;
`;

const HeroTitle = styled.p`
  font-weight: bold;
  font-size: 20px;
  line-height: 24px;
  text-align: center;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textLight};
`;

const Materials = styled.p`
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: ${({ theme }) => theme.sizes.xxxs}px;
`;

const HeroInfo = styled.p`
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  text-align: center;
  letter-spacing: 0.2px;
  color: ${({ theme }) => theme.colors.textLight};
  // for overlaying background which is position: absolute
  position: relative;
`;

const Subheader = styled.div`
  padding: 32px 24px 40px;
  padding-top: 4px;
  text-align: center;
  background: ${({ theme }) => theme.colors.compressionOverlayStickySubheader};
  // for background positioning
  position: relative;
  overflow: hidden;

  ${desktop`
    padding: ${({ theme }) => theme.sizes.xxxl}px 90px;
    padding-top: 4px;
  `}
`;

const CloseButton = styled(Button).attrs({ variant: 'unstyled' })`
  color: ${({ theme }) => theme.colors.textLight};
  position: absolute;
  right: 24px;
  top: 24px;

  &:hover {
    cursor: pointer;
  }

  svg {
    width: 11px;
    height: 11px;
  }
`;

const StyledAssetContainer = styled(AssetContainer)`
  width: 100%;
`;

// note that there is a <Sticky /> component that can usually be used
const CompressionOverlayStickyHeader = ({ content, close }) => {
  const [isSticky, setIsSticky] = useState();
  const { isMobile } = useBreakpoint();
  const ref = useRef();
  const [topOffset, setTopOffset] = useState(0);
  const heroRef = useRef();

  useEffect(() => {
    // when intersecting the top of the screen, sticky is happening
    // use the isSticky value to un-round border radius when sticky
    const cachedRef = ref.current;
    const observer = new IntersectionObserver(
      ([e]) => setIsSticky(e.intersectionRatio < 1),
      { threshold: [1] }
    );

    observer.observe(cachedRef);

    return function () {
      observer.unobserve(cachedRef);
    };
  }, []);

  const handleResize = () => {
    setTopOffset(heroRef.current?.getBoundingClientRect().height ?? 0);
  };

  useLayoutEffect(() => {
    // in order to scroll part of the fabric image underneath the sticky header
    // it is included in the markup twice (2nd time in subheader), and offset to look
    // like one image. on resize events, recalculate the top offset based on sticky header height
    window.addEventListener('resize', handleResize, false);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Header ref={ref} $isSticky={isSticky}>
        <AssetBackground ref={heroRef}>
          <StyledAssetContainer
            name={
              isMobile
                ? content.heroContent.fabric.mobile
                : content.heroContent.fabric.desktop
            }
            aspectRatio="auto"
          />
        </AssetBackground>
        <HeroContent>
          <HeroTitle>{content.heroContent.text.message}</HeroTitle>
          <Materials>{content.heroContent.materials.message}</Materials>
        </HeroContent>
        <CloseButton onClick={close} data-testid="close-compression-modal">
          <CloseIcon aria-hidden />
          <VisuallyHidden>
            <FormattedMessage
              defaultMessage="close"
              id="compression_guide.close"
            />
          </VisuallyHidden>
        </CloseButton>
      </Header>
      <Subheader>
        <SubheaderAssetWrapper $top={topOffset}>
          <StyledAssetContainer
            name={
              isMobile
                ? content.heroContent.fabric.mobile
                : content.heroContent.fabric.desktop
            }
            aspectRatio="auto"
          />
        </SubheaderAssetWrapper>
        <HeroInfo>{content.heroContent.info.message}</HeroInfo>
      </Subheader>
    </>
  );
};

const TranslationType = PropTypes.shape({
  message: PropTypes.node.isRequired,
});

CompressionOverlayStickyHeader.propTypes = {
  close: PropTypes.func,
  content: PropTypes.shape({
    heroContent: PropTypes.shape({
      fabric: PropTypes.shape({
        mobile: PropTypes.string.isRequired,
        desktop: PropTypes.string.isRequired,
      }),
      text: TranslationType.isRequired,
      materials: TranslationType.isRequired,
      info: TranslationType.isRequired,
    }),
  }),
};

export default CompressionOverlayStickyHeader;
